import React from 'react';
import { Loader2 } from 'lucide-react';

export interface LoadingStateProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  fullScreen?: boolean;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  size = 'md',
  fullScreen = false,
}) => {
  const spinnerSize = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  const container = (
    <div className="flex flex-col items-center justify-center p-8 text-center space-y-3">
      <Loader2 className={`${spinnerSize[size]} text-[#1fb4ac] animate-spin`} />
      {message && <p className="text-xs sm:text-sm font-semibold text-[#5F6368]">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 bg-[#F7FAFA]/90 backdrop-blur-xs flex items-center justify-center">
        {container}
      </div>
    );
  }

  return container;
};

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular' | 'card';
  width?: string | number;
  height?: string | number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  className = '',
  ...props
}) => {
  const variantStyles = {
    text: 'h-4 w-full rounded-md',
    circular: 'rounded-full',
    rectangular: 'rounded-xl',
    card: 'h-36 w-full rounded-2xl border border-[#E5E9E9]',
  };

  return (
    <div
      className={`bg-[#E5E9E9]/70 animate-pulse ${variantStyles[variant]} ${className}`}
      style={{ width, height }}
      {...props}
    />
  );
};

export interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message = 'Failed to load content. Please check your network connection and try again.',
  onRetry,
  className = '',
}) => {
  return (
    <div className={`flex flex-col items-center justify-center text-center p-8 bg-[#FDEDEF]/40 rounded-3xl border border-[#D94A5C]/30 my-4 space-y-4 ${className}`}>
      <div className="w-12 h-12 rounded-2xl bg-[#FDEDEF] text-[#D94A5C] flex items-center justify-center font-extrabold text-xl">
        !
      </div>
      <div className="max-w-md space-y-1">
        <h3 className="text-base font-bold text-[#111111]">{title}</h3>
        <p className="text-xs text-[#5F6368] leading-relaxed">{message}</p>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-[#D94A5C] hover:bg-[#c23b4c] text-white font-bold text-xs rounded-xl transition-all cursor-pointer shadow-xs"
        >
          Try Again
        </button>
      )}
    </div>
  );
};
